import {Box, Button, Grid, Paper, Rating, Stack, Typography} from '@mui/material';
import {Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, PlayArrow, Timer} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import '../../styles/components/quiz-grid.css';
import {generateLobbyCode} from '../../utils/generateLobbyCode';
import {collection, deleteDoc, doc, getDoc, setDoc} from 'firebase/firestore';
import {useAuth} from '../../contexts/AuthContext';
import {useLoading} from '../../contexts/LoadingContext';
import {useError} from '../../contexts/ErrorContext';
import {db} from '../../firebase-config';
import {leaveCurrentLobby} from '../../utils/lobbyUtils';

// Importiere die Quiz-Bilder
import exampleQuiz from '../../img/quiz/example-quiz.jpg';
import deutschlandQuiz from '../../img/quiz/deutschland-quiz.jpg';
import fortniteQuiz from '../../img/quiz/fortnite-battle-royale-quiz.jpg';
import harryPotterQuiz from '../../img/quiz/harry-potter-quiz.jpg';
import internetMemeQuiz from '../../img/quiz/internet-meme-quiz.jpg';
import kurioseFaktenQuiz from '../../img/quiz/kuriose-fakten-quiz.jpg';
import marvelSuperheldenQuiz from '../../img/quiz/marvel-superhelden-quiz.jpg';
import pixarFilmquiz from '../../img/quiz/pixar-filmquiz.jpg';
import skibidiToiletQuiz from '../../img/quiz/skibidi-toilet-quiz.jpg';
import defaultQuizImage from '../../img/quiz/default.jpg';

export default function QuizGrid({ quizzes, showEditControls, onQuizDelete }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { setLoading } = useLoading();
  const { setError } = useError();

  const getQuizImage = (quiz) => {
    // Wenn es ein benutzerdefiniertes Quiz mit imageUrl ist
    if (quiz.createdBy && quiz.imageUrl) {
      return quiz.imageUrl;
    }

    // Für vordefinierte Quizzes
    switch (quiz.id) {
      case 'example-quiz': return exampleQuiz;
      case 'deutschland-quiz': return deutschlandQuiz;
      case 'fortnite-battle-royale-quiz': return fortniteQuiz;
      case 'harry-potter-quiz': return harryPotterQuiz;
      case 'internet-meme-quiz': return internetMemeQuiz;
      case 'kuriose-fakten-quiz': return kurioseFaktenQuiz;
      case 'marvel-superhelden-quiz': return marvelSuperheldenQuiz;
      case 'pixar-filmquiz': return pixarFilmquiz;
      case 'skibidi-toilet-quiz': return skibidiToiletQuiz;
      default: return defaultQuizImage;
    }
  };

  const getDifficultyColor = (difficulty) => {
    if (!difficulty) return 'var(--color-neutral)';
    
    switch(difficulty.toLowerCase()) {
      case 'einfach':
      case 'easy':
        return 'var(--color-success)';
      case 'mittel':
      case 'medium':
        return 'var(--color-warning)';
      case 'schwer':
      case 'hard':
        return 'var(--color-error)';
      default:
        return 'var(--color-neutral)';
    }
  };

  const getLocalizedDifficulty = (difficulty) => {
    if (!difficulty) return 'Nicht angegeben';
    
    switch(difficulty.toLowerCase()) {
      case 'easy':
        return 'Einfach';
      case 'medium':
        return 'Mittel';
      case 'hard':
        return 'Schwer';
      default:
        return difficulty;
    }
  };

  const handleQuizSelect = async (quiz) => {
    try {
      if (!currentUser) {
        setError('Sie müssen eingeloggt sein, um ein Quiz zu starten.');
        return;
      }

      setLoading(true);
      setError('');

      // Generiere einen eindeutigen Lobby-Code
      const lobbyCode = generateLobbyCode();

      // Lade die vollständigen Quiz-Daten
      const quizDoc = await getDoc(doc(db, 'quizzes', quiz.id));
      if (!quizDoc.exists()) {
        setError('Quiz nicht gefunden');
        return;
      }
      const quizData = quizDoc.data();

      // Erstelle das Quiz-Update-Objekt
      const quizUpdate = {
        quizId: quiz.id,
        currentQuestion: 0,
        startedAt: null,
        endedAt: null,
        title: quizData.title,
        description: quizData.description
      };

      // Füge imageUrl nur hinzu, wenn es existiert
      if (quizData.imageUrl) {
        quizUpdate.imageUrl = quizData.imageUrl;
      }

      // Erstelle die Game-Session
      const gameSessionRef = doc(collection(db, 'game_sessions'), lobbyCode);
      await setDoc(gameSessionRef, {
        status: 'waiting',
        createdAt: new Date(),
        gameMasterId: currentUser.uid,
        gameMasterParticipates: true,
        currentQuiz: quizUpdate,
        players: {
          [currentUser.uid]: {
            displayName: currentUser.displayName || 'Unbenannter Spieler',
            photoURL: currentUser.photoURL,
            isReady: false,
            score: 0,
            answers: [],
            correctAnswers: 0,
            isGameMaster: true,
            initialized: true
          }
        }
      });

      // Verlasse alle anderen Lobbys, aber nicht die gerade erstellte
      await leaveCurrentLobby(currentUser.uid, lobbyCode);

      // Navigiere zur Lobby
      navigate(`/lobby/${lobbyCode}`);
    } catch (error) {
      console.error('Error creating lobby:', error);
      setError('Fehler beim Erstellen der Lobby: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    try {
      if (!currentUser) {
        setError('Sie müssen eingeloggt sein, um ein Quiz zu löschen.');
        return;
      }

      setLoading(true);
      setError('');

      // Lösche das Quiz aus der Datenbank
      await deleteDoc(doc(db, 'quizzes', quizId));

      // Informiere die übergeordnete Komponente über die Löschung
      if (onQuizDelete) {
        onQuizDelete(quizId);
      }

      setError('Quiz erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting quiz:', error);
      setError('Fehler beim Löschen des Quiz: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="quiz-grid-container" sx={{ mb: 6 }}>
      <Grid container spacing={3}>
        {quizzes.map((quiz) => (
          <Grid item xs={12} sm={6} md={4} key={quiz.id}>
            <Paper className="quiz-grid-item">
              {showEditControls && quiz.createdBy === currentUser?.uid && (
                <Box className="quiz-grid-controls">
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/quiz/edit/${quiz.id}`);
                    }}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<DeleteIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteQuiz(quiz.id);
                    }}
                  >
                    Löschen
                  </Button>
                </Box>
              )}
              <Box
                className="quiz-grid-image"
                sx={{
                  backgroundImage: `url(${getQuizImage(quiz)})`
                }}
              >
                <Box 
                  className="quiz-grid-difficulty-chip"
                  sx={{
                    backgroundColor: getDifficultyColor(quiz.difficulty)
                  }}
                >
                  {getLocalizedDifficulty(quiz.difficulty)}
                </Box>

                <Box className="quiz-grid-image-content">
                  <Typography className="quiz-grid-title">
                    {quiz.title || 'Unbenanntes Quiz'}
                  </Typography>

                  <Box className="quiz-grid-stats">
                    <Box className="quiz-grid-rating">
                      <Rating 
                        value={quiz.stats?.averageRating || 0}
                        precision={0.5}
                        readOnly
                        size="small"
                      />
                      <Typography 
                        variant="body2" 
                        className="quiz-grid-stats-text"
                      >
                        ({quiz.stats?.totalRatings || 0})
                      </Typography>
                    </Box>

                    <Box className="quiz-grid-stats-group">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Timer className="quiz-grid-stats-icon" fontSize="small" />
                        <Typography variant="body2" className="quiz-grid-stats-text">
                          {quiz.questions?.length || 0} Fragen
                        </Typography>
                      </Stack>
                      
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PlayArrow className="quiz-grid-stats-icon" fontSize="small" />
                        <Typography variant="body2" className="quiz-grid-stats-text">
                          {quiz.stats?.totalPlays || 0}x gespielt
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="quiz-grid-content">
                <Typography className="quiz-grid-description">
                  {quiz.description || 'Keine Beschreibung verfügbar'}
                </Typography>

                <Box className="quiz-grid-actions">
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/quiz/${quiz.id}`)}
                  >
                    Details
                  </Button>
                  <Button
                    variant="contained"
                    className="quiz-grid-play-button"
                    onClick={() => handleQuizSelect(quiz)}
                    startIcon={<PlayArrow />}
                  >
                    Quiz wählen
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
        {showEditControls && (
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              className="quiz-grid-item create-quiz" 
              onClick={() => navigate('/quiz/create')}
            >
              <Box className="quiz-grid-create-content">
                <AddIcon className="quiz-grid-create-icon" />
                <Typography variant="h6">
                  Quiz erstellen
                </Typography>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
} 